<template>
  <div v-if="allLessons.length || filteredInvites.length || displayEmptyState" class="lessons-section">
    <h2 v-if="displayTitle" class="section-heading">Lessons</h2>
    <div class="lessons-controls">
      <ion-segment
        v-if="allLessons.length || filteredInvites.length" v-model="subPage" scrollable="true"
        data-testid="lessons-statuses"
        color="success"
        mode="md"
      >
        <template v-for="state in STATES" :key="state">
          <ion-segment-button v-if="displayEmptyState || filterLessons(state).length" :value="state" :data-testid="`lessons-status-${state}`">
            <ion-label class="ion-text-capitalize">
              {{ state }}
              <small v-if="filterLessons(state).length">
                {{ filterLessons(state).length }}
              </small>
            </ion-label>
          </ion-segment-button>
        </template>
      </ion-segment>

      <div v-if="lessonsWithNotes.length && subPage == 'completed'" class="toggle-container">
        <ion-toggle v-model="showOnlyWithNotes" label-placement="start">
          Show only lessons with notes
        </ion-toggle>
      </div>
    </div>

    <template v-if="filterLessons(subPage).length == 0">
      <br>
      <ion-card class="lessons-empty-card">
        <ion-icon :icon="warningOutline" />
        <ion-card-header>
          <ion-card-title class="ion-text-capitalize">No {{ subPage }} Lessons</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <p v-if="userStore.is.student">Click "Book" to book a new lesson.</p>
          <p v-if="userStore.is.instructor">You will be notified when a student books a lesson with you.</p>
        </ion-card-content>
      </ion-card>
    </template>

    <ion-list v-if="filterLessons(subPage).length > 0" class="lesson-list" lines="none">
      <ion-item-group v-for="([month, lessons]) in lessonsGroupsByMonths" :key="month" class="lessons-group">
        <ion-item-divider v-if="lessons.length > 0" sticky="true" class="lesson-divider">
          <ion-label>{{ month }}</ion-label>
        </ion-item-divider>
        <template v-for="lesson in lessons">
          <lesson-item
            v-if="lesson._type === 'lesson'" :key="lesson.id" :lesson="lesson"
            :current-user="userStore.is.type"
            :router-link="{name: 'lessonPage', params: {lessonId: lesson.id}}"
            :display-user="displayUser"
            :display-notes="showOnlyWithNotes"
          />
          <invite-item
            v-else :key="`invite-${lesson.id}`" :invite="lesson"
            :display-user="!props.filterByUser"
          />
        </template>
      </ion-item-group>
    </ion-list>
    <p v-if="displayStateDescription" class="ion-padding-horizontal">
      <ion-note>
        <span v-if="subPage == 'pending'">Lessons awaiting confirmation or rescheduling</span>
      </ion-note>
    </p>
  </div>
</template>

<script setup>
import {
  IonCard, IonCardContent, IonCardHeader, IonCardTitle,
  IonIcon, IonItemDivider, IonItemGroup, IonLabel, IonList, IonNote, IonSegment,
  IonSegmentButton, IonToggle,
} from '@ionic/vue'
import { computed, ref, watchEffect } from 'vue'
import { groupBy } from 'lodash'
import { warningOutline } from 'ionicons/icons'

import InviteItem from '@/shared/components/InviteItem.vue'
import { LessonInvitesRepository } from '@/data/LessonInvite'
import LessonItem from '@/views/golfers/lessons/LessonItem.vue'
import { LessonsRepository } from '@/data/LessonsRepository'
import { useUserStore } from '@/stores/user'

const props = defineProps({
  // displayBookButton: { required: false, type: Boolean, default: true },
  filterByUser: { required: false, type: Object, default: null },
  filterByPackage: { required: false, type: Object, default: null },
  displayEmptyState: { required: false, type: Boolean, default: false },
  displayStateDescription: { required: false, type: Boolean, default: true },
  displayTitle: { required: false, type: Boolean, default: false },
})

const STATES = ['pending', 'upcoming', 'completed']

const userStore = useUserStore()

const lessonsRepo = new LessonsRepository()
const InvitesRepo = new LessonInvitesRepository()

const subPage = ref('upcoming')
let allLessons
if (props.filterByUser) {
  STATES.push('cancelled')
  allLessons = lessonsRepo.getLessonsWithStudent(props.filterByUser.id)
} else if (props.filterByPackage) {
  STATES.push('cancelled')
  allLessons = lessonsRepo.getLessonsWithPackage(props.filterByPackage)
} else {
  allLessons = lessonsRepo.getUserLessons()
}

const displayUser = !props.filterByPackage && !props.filterByUser

const invites = InvitesRepo.getInvites()
const filteredInvites = computed(() => {
  if (props.filterByUser) {
    return invites.value.filter(invite => invite.instructorId === props.filterByUser.id || invite.studentId === props.filterByUser.id)
  }
  if (props.filterByPackage) {
    // Invites don't belong to any package right now, so no invites will be shown on the packages page
    return []
  }
  // hides invites in the past
  return invites.value.filter(invite => invite.datetime >= new Date())
})

const lessonsGrouped = computed(() => {
  return groupBy(allLessons.value, lesson => lesson.stateGroup())
})
const invitesGrouped = computed(() => {
  return groupBy(filteredInvites.value, invite => invite.inviteState)
})

watchEffect(() => {
  if (lessonsGrouped.value) {
    let firstStateWithLessons = STATES.find(state => lessonsGrouped.value[state]?.length > 0)
    firstStateWithLessons ||= 'upcoming'
    if (firstStateWithLessons === 'completed' && !props.filterByPackage && !props.filterByUser) {
      firstStateWithLessons = 'upcoming'
    }
    subPage.value = firstStateWithLessons
  }
  if (userStore.is.student && invitesGrouped.value['pending']?.length) {
    subPage.value = 'pending'
  }
})

const lessonsWithNotes = computed(() => {
  return filterLessons(subPage.value).filter(lesson => lesson._type === 'lesson' && lesson.hasInstructorNotes())
})

const showOnlyWithNotes = ref(false)

function filterLessons(state) {
  const lessons = (lessonsGrouped.value[state] || []).sort((lesson1, lesson2) => lesson1.datetime - lesson2.datetime)
  const invites = (invitesGrouped.value[state] || []).sort((invite1, invite2) => invite1.datetime - invite2.datetime)
  lessons.forEach(lesson => lesson._type = 'lesson')
  invites.forEach(invite => invite._type = 'invite')
  const result = [...lessons, ...invites]
  if (state === 'completed') result.reverse()

  return result
}

const lessonsGroupsByMonths = computed(() => {
  const lessonsGroups = filterLessons(subPage.value).reduce((acc, lesson) => {
    const date = new Date(lesson.datetime)
    const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    acc[monthYear] ||= []
    if (!showOnlyWithNotes.value || subPage.value !== 'completed' || (lesson._type === 'lesson' && lesson.hasInstructorNotes())) {
      acc[monthYear].push(lesson)
    }
    return acc
  }, {})

  // most closest to today first: most recent lessons for "completed", nearest future for "all other states"
  const comparisonFunction = subPage.value === 'completed' ? (a, b) => b[0].localeCompare(a[0]) : (a, b) => a[0].localeCompare(b[0])

  return Object.entries(lessonsGroups).sort(comparisonFunction).map(([monthYear, lessons]) => {
    const monthName = new Date(`${monthYear}-15`).toLocaleString('en-US', { month: 'long', year: 'numeric' })
    return [monthName, lessons]
  })
})
</script>

<style scoped>
ion-badge {
  font-weight: normal;
  margin-left: -3px;
  font-size: 10px;
}

.lessons-section {
  padding: 0 1rem 2rem;
}

.section-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.section-heading h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #13261e;
}

.section-heading ion-note {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(19, 38, 30, 0.55);
}

.lessons-controls {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.toggle-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-right: 0.25rem;
}

.toggle-container :deep(ion-toggle) {
  --handle-background-checked: #ffffff;
  --handle-background: #ffffff;
  --handle-border-radius: 999px;
  --track-background: rgba(19, 38, 30, 0.25);
  --track-background-checked: #0f422a;
  --track-border-radius: 999px;
  font-size: 0.9rem;
}

.lessons-empty-card {
  text-align: center;
  border-radius: 18px;
  padding: 1.75rem 1rem 1.2rem;
  background: linear-gradient(135deg, #ffffff, #f6f8f5);
  box-shadow: 0 18px 32px rgba(16, 38, 27, 0.08);
}

.lessons-empty-card ion-icon {
  font-size: 3.2rem;
  color: rgba(15, 66, 42, 0.35);
  margin-bottom: 0.4rem;
}

.lesson-list {
  background: transparent;
  padding: 0;
  margin-top: 1.2rem;
}

.lessons-group {
  margin-bottom: 1.5rem;
}

.lesson-divider {
  --background: transparent;
  --padding-start: 0;
  border: none;
  margin-bottom: 0.65rem;
  font-size: 0.9rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(19, 38, 30, 0.55);
}

.lesson-list :deep(ion-item) {
  --background: linear-gradient(135deg, #ffffff, #f8faf8);
  --border-radius: 18px;
  --padding-start: 1.25rem;
  --inner-padding-end: 1.25rem;
  --min-height: 110px;
  --border-width: 0;
  --inner-border-width: 0;
  margin-bottom: 1rem;
  transition: transform 160ms ease, box-shadow 160ms ease;
}

.lesson-list :deep(ion-item:hover),
.lesson-list :deep(ion-item:focus) {
  transform: translateY(-2px);
}

.lesson-list :deep(ion-item ion-label) {
  margin: 0;
}

.lesson-list :deep(ion-note) {
  font-size: 0.85rem;
  letter-spacing: 0.03em;
}
</style>
